import { onBeforeMount, onBeforeUnmount } from 'vue';
import { Packages } from '@/services/packages';
import { useSiteDataOnClient } from '~/composables/useSiteDataOnClient';
import { ErrorCode } from '#shared/types/errors';
import { isSessionExpired } from '@/utils/session';
import { useTranslate } from '@zotlo-shared/composable/useTranslate';
import { type Page, PageType } from '@zotlo-shared/types/model';
import { $wait } from '@zotlo-shared/utils/wait';
import { globalListener, globalRemoveListener } from '@zotlo-shared/utils/helpers';
import { useDocLinks } from './useDocLinks';
import { useButtonActions } from './useButtonActions';

export function usePaywall(page: Ref<Page>) {
  const { translate } = useTranslate();

  const { nextPage, language: locale, isPreview, isSandbox, countryCode } = useSiteDataOnClient();

  const pageData = ref({
    packagesLoaded: false,
    packages: [],
    isSubmitLoading: false,
    isSubmitDisabled: false,
    docLinks: useDocLinks(),
    countryCode: countryCode.value,
  });

  const { generateButtonActions } = useButtonActions();

  function checkCurrentSession() {
    if (isSessionExpired()) {
      globalThis?.location?.reload();
      return true;
    }

    return false;
  }

  function setSubmitLoading(isLoading = true) {
    pageData.value.isSubmitLoading = isLoading;
    pageData.value.isSubmitDisabled = isLoading;
  }

  async function handleSubmitPackage(packageId: number) {
    setSubmitLoading();
    if (isPreview.value && !isSandbox.value) return goToPageWithPreviewCountry(nextPage.value!, countryCode.value);
    try {
      checkCurrentSession();
      const response = await Packages.selectPackage({ packageId }, {
        hideErrorToastFor: [ErrorCode.USER_ALREADY_SUBSCRIBED_ERROR]
      });

      const { meta } = response?.data || {};

      if (meta.httpStatus === 200) {
        setVisitedPages(PageType.PAYWALL);
        if (isSandbox.value) return goToPageWithPreviewCountry(nextPage.value!, countryCode.value);
        return goToPage(nextPage.value!);
      }

      if (meta.errorCode === ErrorCode.USER_ALREADY_SUBSCRIBED_ERROR) {
        setSubmitLoading(false);
        return showToast(translate('generate.templates.paymentSuccess.userAlreadySubscribedWarning', locale.value), '', 'error');
      }

      if (meta.errorCode) {
        setSubmitLoading(false);
      }
    } catch (ex) {
      setSubmitLoading(false);
      Promise.reject(ex);
    }
  }

  function setPackagesLoading(isLoading = true) {
    pageData.value.packagesLoaded = !isLoading;
    return isLoading ? $wait.start('initPackages') : $wait.end('initPackages');
  }

  async function getFlowPackages() {
    try {
      setPackagesLoading();
      const response = await Packages.getPackages();
      pageData.value.packages = response?.data?.result?.packages || [];
    } catch (ex) {
      Promise.reject(ex);
    } finally {
      setPackagesLoading(false);
    }
  }

  function onPlanSelectionSubmitted(packageId:any) {
    handleSubmitPackage(packageId as number);
  }

  async function init() {
    getFlowPackages();
  }

  function attachSubmitFnForButtonActions(fn: () => void) {
    generateButtonActions({
      goToPayment: fn
    });
  }

  onBeforeMount(() => {
    globalListener('planSelectionSubmitted', onPlanSelectionSubmitted);
    globalListener('getPlanSelectionHandleSubmit', attachSubmitFnForButtonActions);
    init()
  });

  onBeforeUnmount(() => {
    globalRemoveListener('planSelectionSubmitted', onPlanSelectionSubmitted);
    globalRemoveListener('getPlanSelectionHandleSubmit', attachSubmitFnForButtonActions);
  });

  return { page, pageData };
}
